/**
 * Which step of a pipeline execution holds the content that counts.
 *
 * Lives here because the client and the server must agree on it. They did not:
 * the worker resolved the flagged step when synthesising GeneratedContent,
 * while the execution list showed `stepExecutions[length - 1]`. On any
 * pipeline ending in a review or critique step those are different steps, so
 * the Content button previewed the review's commentary while the draft was
 * what actually got published.
 */

/** The step fields this resolution needs, from either a snapshot or an
 *  execution row. Structural so both shapes satisfy it. */
export interface FinalOutputStep {
  stepOrder: number;
  stepName?: string | null;
  outputText?: string | null;
}

export interface FinalOutputSnapshotStep {
  stepOrder: number;
  isFinalOutput?: boolean | null;
}

/**
 * A step whose name marks it as commentary about the content rather than the
 * content itself. Used only as a fallback when nothing is explicitly flagged —
 * naming is a weak signal, which is exactly why the flag exists.
 */
function isCommentaryStep(step: FinalOutputStep): boolean {
  const name = (step.stepName ?? "").toLowerCase();
  return name.includes("review") || name.includes("critique");
}

/**
 * The step order designated as the pipeline's output.
 *
 * Resolution order, matching the worker exactly:
 *   1. the step the pipeline snapshot flags `isFinalOutput`
 *   2. otherwise the last step that is not a review or critique
 *   3. otherwise the last step
 *
 * Returns null when there are no steps at all.
 */
export function finalOutputStepOrder(
  steps: FinalOutputStep[],
  snapshotSteps: FinalOutputSnapshotStep[] = [],
): number | null {
  if (steps.length === 0) return null;

  const flagged = snapshotSteps.find((s) => s.isFinalOutput === true);
  if (flagged) {
    // Trusted only when a matching execution row exists: a snapshot can name
    // a step order that was later removed from the pipeline.
    if (steps.some((s) => s.stepOrder === flagged.stepOrder)) return flagged.stepOrder;
  }

  const ordered = [...steps].sort((a, b) => a.stepOrder - b.stepOrder);
  const lastSubstantive = [...ordered].reverse().find((s) => !isCommentaryStep(s));

  return (lastSubstantive ?? ordered[ordered.length - 1]!).stepOrder;
}

/**
 * The step execution holding the output to display or publish.
 *
 * Falls through to the last step that actually produced text when the
 * designated one is empty — an execution that failed part-way still has
 * something worth showing, and returning nothing would read as "no content"
 * rather than "did not get that far".
 */
export function finalOutputStep<T extends FinalOutputStep>(
  steps: T[],
  snapshotSteps: FinalOutputSnapshotStep[] = [],
): T | null {
  const order = finalOutputStepOrder(steps, snapshotSteps);
  if (order === null) return null;

  const designated = steps.find((s) => s.stepOrder === order);
  if (designated?.outputText) return designated;

  const withOutput = [...steps]
    .sort((a, b) => a.stepOrder - b.stepOrder)
    .reverse()
    .find((s) => s.outputText);

  return withOutput ?? designated ?? null;
}
